import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Save, AlertCircle } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';

export default function GeneralSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    hospital_name_en: '',
    hospital_name_bn: '',
    hospital_name_color_en: '#1e40af',
    hospital_name_color_bn: '#1e40af',
    phone: '',
    email: '',
    website: '',
    address: '',
    invoice_note: '',
    invoice_prefix: 'INV',
    refer_commission_type: 'percentage' as 'percentage' | 'amount',
    pathology_room: 'Pathology',
    usg_room: 'USG',
    xray_room: 'X-Ray',
    mri_room: 'MRI',
    ecg_room: 'ECG',
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        hospital_name_en: settings.hospital_name_en || '',
        hospital_name_bn: settings.hospital_name_bn || '',
        hospital_name_color_en: settings.hospital_name_color_en || '#1e40af',
        hospital_name_color_bn: settings.hospital_name_color_bn || '#1e40af',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        address: settings.address || '',
        invoice_note: settings.invoice_note || '',
        invoice_prefix: settings.invoice_prefix || 'INV',
        refer_commission_type: settings.refer_commission_type || 'percentage',
        pathology_room: settings.pathology_room || 'Pathology',
        usg_room: settings.usg_room || 'USG',
        xray_room: settings.xray_room || 'X-Ray',
        mri_room: settings.mri_room || 'MRI',
        ecg_room: settings.ecg_room || 'ECG',
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const result = await updateSettings(formData);

    if (result.success) {
      toast.success('General settings saved successfully');
    } else {
      toast.error(result.error || 'Failed to save settings');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Hospital Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hospital Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hospital Name (English) *
            </label>
            <input
              type="text"
              required
              value={formData.hospital_name_en}
              onChange={(e) => setFormData({ ...formData, hospital_name_en: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Hospital Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hospital Name (Bengali)
            </label>
            <input
              type="text"
              value={formData.hospital_name_bn}
              onChange={(e) => setFormData({ ...formData, hospital_name_bn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="হাসপাতালের নাম"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name Color (English)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.hospital_name_color_en}
                onChange={(e) => setFormData({ ...formData, hospital_name_color_en: e.target.value })}
                className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hospital_name_color_en}
                onChange={(e) => setFormData({ ...formData, hospital_name_color_en: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="#1e40af"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name Color (Bengali)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.hospital_name_color_bn}
                onChange={(e) => setFormData({ ...formData, hospital_name_color_bn: e.target.value })}
                className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hospital_name_color_bn}
                onChange={(e) => setFormData({ ...formData, hospital_name_color_bn: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="#1e40af"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="+880 1234567890"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="info@hospital.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="https://hospital.com"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Hospital address..."
            />
          </div>
        </div>
      </div>

      {/* Invoice Configuration */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Prefix (3 digits) *
            </label>
            <input
              type="text"
              required
              maxLength={3}
              value={formData.invoice_prefix}
              onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="INV"
            />
            <p className="mt-1 text-xs text-gray-500">
              Invoice numbers will be formatted as: {formData.invoice_prefix}-{new Date().getFullYear()}-001
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Referral Commission Type
            </label>
            <select
              value={formData.refer_commission_type}
              onChange={(e) => setFormData({ ...formData, refer_commission_type: e.target.value as 'percentage' | 'amount' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="amount">Fixed Amount (৳)</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Note
            </label>
            <textarea
              value={formData.invoice_note}
              onChange={(e) => setFormData({ ...formData, invoice_note: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Note to appear on invoices..."
            />
          </div>
        </div>
      </div>

      {/* Room Labels */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Room Labels</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pathology Room
            </label>
            <input
              type="text"
              value={formData.pathology_room}
              onChange={(e) => setFormData({ ...formData, pathology_room: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Pathology"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              USG Room
            </label>
            <input
              type="text"
              value={formData.usg_room}
              onChange={(e) => setFormData({ ...formData, usg_room: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="USG"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              X-Ray Room
            </label>
            <input
              type="text"
              value={formData.xray_room}
              onChange={(e) => setFormData({ ...formData, xray_room: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="X-Ray"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MRI Room
            </label>
            <input
              type="text"
              value={formData.mri_room}
              onChange={(e) => setFormData({ ...formData, mri_room: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="MRI"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ECG Room
            </label>
            <input
              type="text"
              value={formData.ecg_room}
              onChange={(e) => setFormData({ ...formData, ecg_room: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="ECG"
            />
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Important Information</p>
          <p>These settings will be used across all invoices, reports, and prescriptions. Make sure to save your changes before switching tabs.</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>
    </form>
  );
}
